import { Input } from "@component/input";
import { EmailTemplate, EmailTemplateRequest } from "@type/setting";
import { Label } from "@component/label";
import { BracesIcon, EditIcon } from "lucide-react";
import { parseFormat } from "@util/date";
import { useUserProfileContext } from "@context/user_profile";
import TextEditor, { TextEditorRef } from "@component/rich-editor/v2/text-editor";
import TemplateActions from "./template-actions";

interface Props {
    isViewMode: boolean;
    onChangeViewMode: (isViewMode: boolean) => void;
    showVariables: boolean;
    selectedTemplate: EmailTemplate;
    onSave: (data: EmailTemplateRequest) => void;
    onCancel: () => void;
    onDelete: (template: EmailTemplate) => void;
    setLastFocusedField: (field: "subject" | "content") => void;
    onShowVariable: () => void;
    subjectRef: React.RefObject<HTMLInputElement | null>;
    contentRef: React.RefObject<TextEditorRef | null>;
    onTemplateChange: (key: string, value: string) => void;
}

export default function EmailTemplateEditForm(props: Props) {
    const [, userInfo] = useUserProfileContext();

    const handleSaveTemplate = () => {
        if (!props.selectedTemplate.template_name.trim() || !props.selectedTemplate.email_subject.trim() || !props.selectedTemplate.email_content.trim()) {
            return;
        }
        props.onSave(props.selectedTemplate);
    }

    return (
        <div className="space-y-4 h-full flex flex-col">
            {/* 1. Template Name + Action Buttons */}
            <div className="flex items-center justify-between flex-shrink-0">
                <div className={props.isViewMode ? "" : "flex-1 mr-4"}>
                    {props.isViewMode ? (
                        <h3 className="text-lg font-semibold text-gray-900">
                            {props.selectedTemplate.template_name}
                        </h3>
                    ) : (
                        <Input
                            id="templateName"
                            value={props.selectedTemplate.template_name}
                            onChange={(e) => props.onTemplateChange("template_name", e.target.value)}
                            onFocus={() => props.setLastFocusedField("content")}
                            className="text-lg font-semibold mt-1"
                            placeholder="Enter template name"
                        />
                    )}
                </div>

                {/* Action Buttons - Rightmost */}
                {userInfo?.organization_role === "write" && (
                    <TemplateActions
                        template={props.selectedTemplate}
                        isViewMode={props.isViewMode}
                        hasChanges={true}
                        onEdit={() => props.onChangeViewMode(false)}
                        onSave={handleSaveTemplate}
                        onCancel={props.onCancel}
                        onDelete={props.onDelete}
                    />
                )}
            </div>

            {/* 2. Last Updated Date and Time */}
            {!props.selectedTemplate.is_default && (
                <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1 flex-shrink-0">
                    <EditIcon className="h-4 w-4" />
                    <span>Last Updated {parseFormat(props.selectedTemplate?.updated_at || "", "D MMM YYYY hh:mm A")}</span>
                </div>
            )}

            {/* 3. Email Subject */}
            <div
                style={{ paddingTop: "16px", paddingBottom: "8px" }}
                className="flex-shrink-0"
            >
                <div className="flex items-center justify-between mb-2">
                    <Label
                        htmlFor="subject"
                        className="text-sm font-medium text-gray-700"
                    >
                        Email Subject
                    </Label>
                </div>
                {props.isViewMode ? (
                    <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
                        {props.selectedTemplate.email_subject || "(No subject set)"}
                    </div>
                ) : (
                    <Input
                        ref={props.subjectRef}
                        value={props.selectedTemplate.email_subject}
                        onChange={(e) => props.onTemplateChange("email_subject", e.target.value)}
                        onFocus={() => props.setLastFocusedField("subject")}
                        className="mt-1"
                        placeholder="Enter email subject line"
                    />
                )}
            </div>

            {/* 4. Email Content */}
            <div
                className="flex flex-col"
                style={{
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    height: "300px",
                }}
            >
                {props.isViewMode ? (
                    <div className="flex flex-col flex-1">
                        <Label className="text-sm font-medium text-gray-700 mb-1">
                            Email Content
                        </Label>
                        <div className="border border-gray-200 rounded-md flex-1 flex flex-col bg-gray-50 min-h-0">
                            <div
                                className="p-4 flex-1 text-sm text-gray-900 whitespace-pre-wrap overflow-y-auto"
                                dangerouslySetInnerHTML={{ __html: props.selectedTemplate.email_content || "(No content set)" }}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col flex-1">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-medium text-gray-700 mb-1">
                                Email Content
                            </Label>
                            <button
                                type="button"
                                onClick={props.onShowVariable}
                                className="text-xs text-blue-600 hover:text-black flex items-center gap-1"
                            >
                                <BracesIcon className="h-3 w-3" />
                                {props.showVariables ? "Hide" : "Show"} Variables
                            </button>
                        </div>
                        <TextEditor
                            ref={props.contentRef}
                            defaultValue={props.selectedTemplate.email_content}
                            onChange={(content) => {
                                props.onTemplateChange("email_content", content);
                            }}
                            onFocus={() => props.setLastFocusedField("content")}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            Click the Variables button to see all available personalization options for your email content.
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}